import React from 'react';
import { html } from 'lit';
import parse from 'html-react-parser';
import { querySelectorDeep } from 'omni-ui';
import { tooltip } from 'omni-ui/omni-tooltip-next.js';

// Shared components and utilities
import { 
    OmniTileComponent, 
    OmniToolbarComponent, 
    OmniSearchComponent, 
    OmniTableStickyComponent, 
    ToggleFeature as toggleFeature,
    exportStatusHtml, 
    NoDataComponent,
    MediaOpsService,
    Utils,
    AlertMessagesService,
    initAmplitude, 
    trackEvent
} from '../../../../shared';

// Feature imports
import { CampaignFilterComponent, MergeCampaignsComponent as MergeCampaigns } from '../..';
import { PlacementDetailsTableService } from '../../../placements';

// Local imports
import { CampaignTableV1Dropdown } from './campaignTableV1Dropdown';

export class CampaignTableV1 extends React.Component {
    defaultState = {
        data: [],
        fetchCount: 0,
        totalCount: 0,
        spinnerCount: 0,
        hasNext: false,
        pagesLoaded: 1,
        isTableNested: false,
        filterOptions: {
            campaignName: '',
            startDate: '',
            endDate: '',
            budgetMin: '',
            budgetMax: '',
            clientName: ''
        },
        isAlertOpen: false,
        modalData: null,
        selectedCampaigns: [],
        displayMergeModal: false,
        enableMerge: false,
        enableUnmerge: false,
        loaded: false,
        sort: { key: '', dir: 'asc' },
        archiveTab: false,
        errorObject: { displayError: false },
        ispaginationCall: false,
        expandAllOnLoad: false,
    };
    scrollPosition = 0;
    isNestedCampaign = false;
    
    constructor(props) {
        super(props);
        this.state = this.defaultState;
        props.setSpinnerCount(0);
        
        // Dynamically bind all methods from MediaOpsDropdown
        Object.keys(CampaignTableV1Dropdown).forEach(method => {
            if (typeof CampaignTableV1Dropdown[method] === 'function') {
            this[method] = CampaignTableV1Dropdown[method].bind(this);
            }
        });

        // Define the close method used in the info dialog
        this.close = (e) => {
            if (e && e.target) {
                const dropdown = e.target.closest('.dropdown');
                if (dropdown) {
                    dropdown.classList.remove('is-active');
                    const button = dropdown.querySelector('button');
                    if (button) button.classList.remove('is-active');
                }
            }
        };
    }

    // navigates to specific path using Utils.navigateToComponent()
    navigateToPath = (campaign, path) => {
        this.props.setSpinnerCount(1);
        let parentCampaign;
        if (campaign?.parent_id) {
            parentCampaign = this.state.data.filter((item) => item.id === campaign.parent_id)[0];
        }
        const tableBody = document.getElementsByClassName("campaign-tile")[0]?.bodyScrollerDiv;
        this.props.setCampaignTableData({tableState: this.state, scrollTop: tableBody.scrollTop});        
        Utils.navigateToComponent(campaign, path, this.props, {}, parentCampaign)
        if (path === 'placementDetailsTable') {
            trackEvent('Navigating to Placements Page from Campaign Listing Page', { access_from: 'Campaign Listing Page' });
        }
    }
    
    /**
     * Handles the change event of a checkbox and the logic for selecting and deselecting campaigns
     * @param {Object} campaign - The campaign object.
     * @param {Event} event - The event object. 
     */
    handleCheckboxChange = (campaign, event) => {
        const target = /** @type {HTMLDivElement} */ (event.target);
        const checkbox = /** @type {HTMLInputElement} */ (
            [...target.children].find((el) => el instanceof HTMLInputElement)
        );
        campaign['checked'] = !checkbox.hasAttribute("checked");
        let selectedCampaigns = [...this.state.selectedCampaigns];
        const isSelected = selectedCampaigns.find((object) => object.id === campaign.id);
        if (campaign.checked && !isSelected) {
            selectedCampaigns.push(campaign);
            if (campaign?.nestedData?.length > 0) {
                campaign.nestedData.forEach((nestedCampaign) => selectedCampaigns.push(nestedCampaign));
            }
        } else if (!campaign.checked && isSelected) {
            selectedCampaigns = selectedCampaigns.filter((object) => object.id !== campaign.id);
            if (campaign?.nestedData && campaign?.nestedData.length > 0) {
                for (const nestedCampaign of campaign.nestedData) {
                    selectedCampaigns = selectedCampaigns.filter((object) => object.id !== nestedCampaign.id);
                }
            }
        }
        this.setState({
            selectedCampaigns: selectedCampaigns,
            enableMerge: (selectedCampaigns.length > 1 && this.checkIfOnePlanitCampaignIsSelectedOrNot(selectedCampaigns)),
            enableUnmerge: (selectedCampaigns.length > 1 && this.checkIfOnlyCampaignsWithChildrenAreSelected(selectedCampaigns))
        });
    }

    /**
     * The below code is checking if a key-value pair exists in any object within the `selectedCampaigns`
     * array. If the key-value pair exists, it then checks if it exists in more than one object. If it
     * exists in only one object and there are no other objects with children selected, it returns `true`.
     * Otherwise, it returns `false`. 
     * @param {Object[]} selectedCampaigns - The campaign objects array.
     */
    checkIfOnePlanitCampaignIsSelectedOrNot = (selectedCampaigns) => {
        // Check if the key value pair exists in any object.
        const hasKeyValuePair = selectedCampaigns && selectedCampaigns.some(obj => obj.hasOwnProperty('source') && obj['source'] === 'planit');
        // If the key value pair exists in any object, check if it exists in more than one object.
        if (hasKeyValuePair) {
            const count = selectedCampaigns.reduce((acc, obj) => (acc + (obj.hasOwnProperty('source') && obj['source'] === 'planit')), 0);
            return count < 2 && !this.checkIfOnlyCampaignsWithChildrenAreSelected(selectedCampaigns);
        }
        return false;
    }

    /**
     * The above code is a JavaScript function that checks if every object in the `selectedCampaigns` array
     * @param {Object[]} selectedCampaigns - The campaign objects array.
     */
    checkIfOnlyCampaignsWithChildrenAreSelected = (selectedCampaigns) => {
        return selectedCampaigns.every(obj => obj.nested === true) && selectedCampaigns.filter(obj => obj.hasOwnProperty('source') && obj['source'] === 'planit')?.length === 1;
    }

    /**
     * Function to handle the export Placement functionality in Actions tab
     * This will call the placement API to get all the placement details and modify that to create an export data for excel
     * @param {object} campaignObj - campaign object
     * sending 'versionOneApi' getplacement call, to find which version of PDMS we are using,then use api as per version
     */
    exportPlacements = (campaignObj) => {
        this.props.setSpinnerCount(1);
        let campaignId = encodeURIComponent(campaignObj.id);
        MediaOpsService.getPlacements(campaignId,null,this.props?.versionOneApi).then((response)=>{
            this.props.setSpinnerCount(0);
            if(response.data.length){
                let exportData = response.data.map((placement)=> {
                    return {
                        "Campaign ID": placement.amd_campaignid,
                        "Campaign Name": campaignObj.campaign_name,
                        "Placement ID": placement.placements_id,
                        "Placement Type": placement.placement_type,
                        "Placement Taxonomy": placement.placement_taxonomy,
                        "Flight Start Date": placement.flight_start,
                        "Flight End Date": placement.flight_end,
                        "Export Status": placement.export_status
                    }
                })
                Utils.exportToExcel(exportData, "PlacementsReport", campaignObj.campaign_name + '_PlacementsReport.xlsx')
                AlertMessagesService.showSuccessToastMessage(`Placements exported successfully for ${campaignObj.campaign_name}`);
                trackEvent(`Placements of Campaign(${campaignObj?.id}) are exported.`, { access_from: 'Campaign Listing Page' });
            } else {
                AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.exportPlacements(campaignObj), title: `Unable to find placement for ${campaignObj.campaign_name}`, bodyMsgArr: []}]);
                trackEvent(`Placements of Campaign(${campaignObj?.id}) are not exported.`, { access_from: 'Campaign Listing Page' });
            }
        }).catch((err)=>{
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.exportPlacements(campaignObj), title: 'Unable to export the placement', bodyMsgArr: []}]);
            trackEvent(`Placements of Campaign(${campaignObj?.id}) are not exported.`, { access_from: 'Campaign Listing Page', event: { error: err?.response?.data } });
        })
    }

    /**
     * Function to handle the archive Campaign functionality in Actions tab
     * This will call the campaign API to update the isArchive flag to true
     * @param {object} campaignObj - campaign object
     */ 
    archiveCampaign = (campaignObj) =>{
        this.props.setSpinnerCount(1);
        let payload = {"isArchive": true};
        let campaignId = encodeURIComponent(campaignObj.id);
        MediaOpsService.updateCampaign(payload, campaignId, this.props?.versionOneApi).then((response)=>{
            this.props.setSpinnerCount(0);
            AlertMessagesService.showSuccessToastMessage(`${campaignObj.campaign_name} has been archived successfully`);
            this.setState({ ...this.defaultState, isTableNested:false}, this.getCampaigns);
            trackEvent('Campaigns are archived.', { access_from: 'Campaign Listing Page' });
        }).catch((error)=>{
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.archiveCampaign(campaignObj), title: `Unable to archive the ${campaignObj.campaign_name}`, bodyMsgArr: []}]);
            trackEvent('Campaigns are not archived.', { access_from: 'Campaign Listing Page', event: { error: error?.response?.data } });
        })
    }

    /**
     * Function to handle the restore Campaign functionality in Actions tab
     * This will call the campaign API to update the isArchive flag to false
     * @param {object} campaignObj - campaign object
     */ 
    unArchiveCampaign = (campaignObj) =>{
        this.props.setSpinnerCount(1);
        let payload = {"isArchive": false};
        let campaignId = encodeURIComponent(campaignObj.id);
        MediaOpsService.updateCampaign(payload, campaignId, this.props?.versionOneApi).then((response)=>{
            this.props.setSpinnerCount(0);
            AlertMessagesService.showSuccessToastMessage(`${campaignObj.campaign_name} has been restored successfully`);
            this.setState({ ...this.defaultState, archiveTab: true, isTableNested:false }, this.getCampaigns);
        }).catch(()=>{
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.unArchiveCampaign(campaignObj), title: `Unable to restore the ${campaignObj.campaign_name}`, bodyMsgArr: []}]);
        })
    }

    leftColumns = [
        {
            label: 'CAMPAIGN',
            key: 'campaignDisplayName',
            isSortable: true,
            passthrough: true,
            isMain: true,
            template: (value) => {
                const campaignName = value?.parent_id ? value?.campaignTaxonomy : value?.campaignDisplayName;

                return html`
                <style>
                    :host::part(td-content) {
                        color: var(--color-grey-600) !important;
                    }

                    :host span {
                        font-size: 14px !important;
                    }

                    :host::part(th-content) {
                        font-size: 12px !important;
                    }

                    :host::part(margin-left-20) {
                        margin-left: 20px;
                    }
                    :host::part(margin-left-30) {
                        margin-left: 30px;
                    }
                    :host::part(campaign-withno-children) {
                        height: 47px !important;
                        align-content: flex-end !important;
                    }
                    :host::part(table-body-cell-campaign-name) {
                        padding-left: 0px !important;
                        line-height: 47px !important;
                        border: none !important;
                    }
                    :host::part(wrapText) {
                        display: flex;
                        flex-wrap: wrap;
                        white-space: normal;
                        max-height: 40px;
                        overflow: hidden;
                        word-break: break-all;
                        font-weight: 600 !important;
                        color: var(--color-grey-900) !important;
                        justify-content:flex-start;
                        align-items:center;
                    }
                    .expand-wrapper.is-clamped {
                        max-width: 100% !important;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }
                    .expand-spacer {
                        width: 0px !important;
                        max-width: 0px !important;
                        min-width: 0px !important;
                        margin-right: 10px;
                    }
                    .button.is-text.is-stateless {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        height: 18px !important;
                        width: 18px !important;
                        padding: 0 20px !important;
                        padding-bottom: 5px !important;
                    }
                </style>

                <span part="td-content ${value.marginClass ? value.marginClass : ""} wrapText" class="is-campaign-name"  @click="${this.navigateToPath.bind(this, value, 'campaignDetails')}">${campaignName}</span>`;
            }
        },
        {
            label: 'SOURCE',
            key: 'source',
            default: '',
            isSortable: true,
            template: (value) => {
                return html`<td part="table-body-cell">
                    <span part="td-content">${MediaOpsService.getSource(value)}</span>
                </td>`
            }
        },
        {
            label: 'START DATE',
            key: 'startDate',
            default: '',
            isSortable: true,
            template: (value) => {
                return html`<td part="table-body-cell">
                    <span part="td-content">${Utils.formatDateObject(value)}</span>
                </td>`
            }
        },
        {
            label: 'END DATE',
            key: 'endDate',
            default: '',
            isSortable: true,
            template: (value) => {
                return html`<td part="table-body-cell">
                    <span part="td-content">${Utils.formatDateObject(value)}</span>
                </td>`
            }
        },
        {
            label: 'CHANNELS',
            key: 'channels',
            default: '',
            isSortable: false,
            template: (value) => {
                return html`<td part="table-body-cell">
                <span part="td-content">${MediaOpsService.getChannels(value)}</span>
              </td>`
            }
        },
        {
            label: 'MOH CLIENT CODE',
            key: 'client_code',
            default: '',
            isSortable: false,
            passthrough: false,

        },
        {
            label: 'PLANIT CLIENT CODE',
            key: 'clientid',
            default: '',
            isSortable: false,
            passthrough: true,
            template: (value) => {
                let clientCodeDisplay = '';

                if (value?.clientid) {
                    const uniqueStrings = new Set(
                        Array.isArray(value.clientid)
                            ? value.clientid
                                .filter(item => item != null && typeof item === "string" && item.trim() !== "")
                                .map(item => item.trim())
                            : [value.clientid]
                                .filter(item => item != null && typeof item === "string" && item.trim() !== "")
                                .map(item => item.trim())
                    );
                    clientCodeDisplay = Array.from(uniqueStrings).join(", ") || '';
                }

                return html`
                <td part="table-body-cell">
                    <span part="td-content">${clientCodeDisplay}</span>
                </td>`;
            }
        },
        {
            label: 'PRODUCT CODE',
            key: 'productid',
            default: '',
            isSortable: false,
            passthrough: false,
        },
        {
            label: 'ESTIMATE',
            key: 'estimateid',
            default: '',
            isSortable: false,
            passthrough: false,
        },
        {
            label: 'NET MEDIA',
            key: '',
            default: '',
            isSortable: true,
            passthrough: true,
            template: (value) => {
                return html`
                <td part="table-body-cell">
                <div style="display:flex">
                    <span part="td-content">
                        ${this.props?.systemParametersData?.data?.isLocal ? 
                            value?.campaign_total_budget_local && value?.local_currency_name && Utils.getLocalCurrency(value?.local_currency_name, value?.campaign_total_budget_local) : 
                            Utils.getLocalCurrency('USD', value?.campaign_total_budget)}
                    </span>
                </div>
              </td>`
            }
        },
        {
            label: 'MEDIAOCEAN ID',
            key: 'mediaocean_id',
            default: '',
            isSortable: false,
            passthrough: false,
        },
    ];

    rightColumns = [
        {
            label: 'STATUS',
            key: 'status',
            default: '',
            isSortable: true,
            passthrough: true,
            template: (value) => {
                return html`
                <style>
                :host::part(table-body-cell){
                    padding: 0rem 2.1rem;
                    cursor: pointer;
                }
                :host::part(export-status){
                    display:flex;
                    column-gap:12px;
                    line-height: 16px;
                    font-size: 13px;
                }
                </style>
                <td part="table-body-cell" style="display: inline-block">
                    ${exportStatusHtml.getExportHTML(value,'MOH', value.exportStatusError )}
                    ${exportStatusHtml.getExportHTML(value,'MO', value.exportStatusError )}
                    ${exportStatusHtml.getExportHTML(value,'TAXONOMY', value.exportStatusError )}
                </td>`; 
            }
        },
        {
            label: 'ACTIONS',
            key: 'actions',
            passthrough: true,
            template: (value, index) => {
                // Based on archiveTab, we are rendering the action html
                
                if(this.props.isMappingTable){
                   return html `
                    <style>
                        :host::part(hide-Action-select-btn){
                            display:none !important;
                        }
                    </style>
                    <td part="table-body-cell">
                        <button className="button primary" part="${value.parent_id ? 'hide-Action-select-btn' : ''}" @click="${() => this.openMappingMModal(value)}">Select</button>
                   </td>`;  
                }else{
                    if(!this.state.archiveTab) {
                        return this.getActionsColumnHTML(value, index);
                    } else {
                        return this.getRestoreButtonHTML(value);
                    }
                }
            }
        }
    ];

    componentWillReceiveProps(props) {
        if (props?.childCampaignIsCreated) {
            this.getCampaigns();
            this.props?.setChildCampaignIsCreated(false);
        }
    };
    
    componentDidMount() {
        if (this.props?.campaignTableData?.tableState && !this.props?.childCampaignIsCreated) {
            this.isNestedCampaign = this.props?.campaignTableData?.tableState?.data?.find(val=> val.child_count) ? true : false;
            this.setState({...this.props.campaignTableData.tableState}, () => {
                setTimeout(() => {
                    this.scrollPosition = this.props?.campaignTableData?.scrollTop;
                    this.updateTableAndScrollAfterLoad();
                });
            });
        } else {
            this.getCampaigns();
            if(this.props?.setChildCampaignIsCreated){ // if the child campaign is created, then set the flag to false
                this.props?.setChildCampaignIsCreated(false);
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.ontableResize);
        let scrollBody = document.getElementsByClassName("campaign-tile")[0]?.bodyScrollerDiv;
        scrollBody?.removeEventListener('scroll', this.handleScroll);
    }

    closeMenu(index, prefix) {
        // Get all dropdowns on the page that aren't hoverable.
        // hide any opned dropdown
        let allDropdownEle = querySelectorDeep('omni-table', 'right-table')
            ?.shadowRoot?.querySelectorAll('.dropdown');
        allDropdownEle && allDropdownEle.forEach(ele => {
            if (ele.classList.contains('is-active') && ele.id !== (prefix + index)) {
                ele.classList.remove('is-active');
                ele.querySelector('button').classList.remove('is-active');
            }
        });
    }

    /**
     * The below code is a JavaScript function that is responsible for toggling the visibility of a dropdown menu.
     * @param {String} id - id of the documents || info options
     * @param {String} prefix - info || documents
     * @param {event} event - click event
     * @param {number} index - options popup index
     */
    onDocumentTriggerClick = (id, prefix, event, index) => {
        // hide any opned dropdown
        this.closeMenu(id, prefix)
        // make the dropdown hide and show
        let el = querySelectorDeep('omni-table', 'right-table')?.shadowRoot?.querySelector('#' + prefix + '-' + id);
        const elButton = el.querySelector('button');
        if (el) {
            el.classList.toggle('is-active');
            elButton.classList.toggle('is-active');
        }
        // move the dropdown to top for last 3 records
        if (this.state.data.length > 6 && index > this.state.data.length - 4 && !el.classList.contains('is-up')) {
            el.classList.add('is-up');
        }
        // position the dropdown
        let dd = querySelectorDeep('omni-table', 'right-table').shadowRoot?.querySelectorAll('.dropdown-menu');
        dd.forEach(ele => {
            if (ele) {
                ele.style.left = prefix === 'documents' ? '-145px' : '-560px';
            }
        });
        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * The below code is defining a function called `resetStateAndUpdateCampaigns` in JavaScript. This
     * function is using the `setState` method to reset the state of the current component to its default
     * state, and then it is calling the `createData` method. 
     */
    resetStateAndUpdateCampaigns = () => {
        this.setState(this.defaultState, () => {
            this.props.setMappingTable(false); 
            this.getCampaigns() 
        })
    }

    /**
     * Creates data for the campaign and retrieves it from the MediaOpsService.
     * If isFreshData is true, it sets the 'loaded' state to false before fetching data.
     * If campaignSentToMediaOcean is true, it checks the export status of the campaign and shows corresponding toast messages.
     * @param {boolean} isFreshData - Indicates whether to fetch fresh data (default is true).
     * @returns {void}
     */
    getCampaigns = (isFreshData = true,  campaignPmdsId = null,campaignSyntheticId = null) => {
        if (isFreshData) {
            this.setState({loaded: false})
        }
        let filterOptions = {...this.state.filterOptions,source:'planned','page-count':10};
        let hasNext = this.state.hasNext;
        if(campaignPmdsId){
            filterOptions = {'source':'children','parent-id': campaignPmdsId};
            hasNext = null;
            if(campaignSyntheticId){
                filterOptions['campaign-id'] = campaignSyntheticId;
            }
        }
        MediaOpsService.getCampaignData(filterOptions, hasNext, this.state.archiveTab).then(response => {
            this.processTableResponse(response, isFreshData, campaignSyntheticId);
             if(isFreshData){
                this.getUnMapCampaign();
            }
        }).catch((error) => {
            this.setState({loaded: true});
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.getCampaigns(isFreshData, campaignPmdsId), title: 'Campaign API cannot be loaded', bodyMsgArr: []}]);
        });
    }
    /**
     * Creates data for the campaign and retrieves it from the MediaOpsService.
     * If isFreshData is true, it sets the 'loaded' state to false before fetching data.
     * If campaignSentToMediaOcean is true, it checks the export status of the campaign and shows corresponding toast messages.
     * @param {boolean} isFreshData - Indicates whether to fetch fresh data (default is true).
     * @returns {void}
     */
    getUnMapCampaign = () => {
        let filterOptions =  {'source':'unmapped'};

        MediaOpsService.getCampaignData(filterOptions,null, this.state.archiveTab).then(response => {
            let unMapCampaign = [];
            let responseData = response['data']['Items'] ? response['data']['Items'] : [];
            let data = MediaOpsService.prepareCampaignData(responseData);
            
            unMapCampaign = [...data];
            // campaigns.push(...data);
            this.setState({ unMapCampaign});
            this.props.setUnMapCampaignObj(unMapCampaign);
            
        }).catch((error) => {
            this.setState({loaded: true});
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.getUnMapCampaign(), title: 'Unmapped Campaign API cannot be loaded', bodyMsgArr: []}]);
        });
    }

    /**
     * Processes the response from the table API and updates the state accordingly.
     * 
     * @param {object} response - The response object from the table API.
     * @param {boolean} isFreshData - Indicates whether the data is fresh or not.
     * @memberof MediaOps
     */
    processTableResponse = (response, isFreshData, parentId) => {
        if (response?.data?.statuscode === 500) {
            const errorObj = JSON.parse(response?.data?.body);
            this.setState({ errorObject: { displayError: true } });
        }
        this.parseCampaignResponse(response, isFreshData, parentId);
        setTimeout(() => {
            document.addEventListener('click', (e) => {
                this.closeMenu(e);
            });
        });
    }

    /**
     * The below code is processing a response object and populating an array called `data` with objects
     * containing specific properties from the response data. It then checks a boolean variable
     * `isFreshData` to determine whether to append the `data` array to an existing `campaigns` array or
     * replace it entirely. Finally, it updates the state variables `fetchCount`, `totalCount`, and
     * `hasNext` based on the response data. 
     * @param {Object} response - campaigns
     * @param {Boolean} isFreshData
     */
    parseCampaignResponse = (response, isFreshData, parentId = null) => {
        let campaigns = [];
        let childCampaigns = [];
        let responseData = response['data']['Items'] ? response['data']['Items'] : [];
        let data = MediaOpsService.prepareCampaignData(responseData);
        const hasNext = response?.data?.paging?.next;
        this.iscampaignNestedTree = this.iscampaignNestedTree ? this.iscampaignNestedTree : false;
        if(!parentId){
            data.forEach((campaign, campaignIndex) => {
                if (campaign.child_count) {
                    this.iscampaignNestedTree = true;
                    campaign.nestedData = [];
                } else {
                    campaign.marginClass = "margin-left-30";
                }

                if (campaignIndex === data.length - 1) {
                    if (!!this.state.searchParam || this.state.filterApplied) {
                        this.setState({ expandAllOnLoad: true });
                    } else {
                        this.setState({ expandAllOnLoad: false });
                    }
                }
            });
            if (!isFreshData) {
                campaigns = [...this.state.data, ...data];
                // campaigns.push(...data);
                this.setState({ hasNext });
            } else {
                campaigns = data;
                this.setState({ totalCount: response['data']['total_records'], hasNext });
            }
            campaigns = campaigns.reduce((acc, current) => {
                const exist = acc.find(item => item.id === current.id);
                return exist ? acc : [...acc, current];
            }, []);
        } else {
            campaigns = [...this.state.data];
            childCampaigns = data;
        }

        /* The below code is toggling the sorting direction between 'asc' (ascending) and 'desc' (descending)
        in a React component's state. It first creates a copy of the `sort` object from the component's
        state using the spread operator (`...`). Then, it checks if the `dir` property of the `sortObj` is
        currently set to 'asc'. If it is, it updates the `dir` property to 'desc' and sets the updated
        `sortObj` back to the component's state using `setState()`. If the `dir` property is not 'asc */
        let sortObj = {...this.state.sort}
        if (sortObj?.dir === 'asc') {
            sortObj.dir = 'desc';
            this.setState({sort: sortObj});
        } else {
            sortObj.dir = 'asc';
            this.setState({sort: sortObj});
        }

        this.createNestedData(campaigns, isFreshData, childCampaigns, parentId);
    }

    /**
     * The above code is transforming the data in the `campaigns` array into a nested structure based on
     * the `parent_id` property. 
     * @param {Object[]} campaigns - Array of campaigns
     */
    createNestedData = (campaigns, isFreshData, childCampaigns, parentId) => {
        const setIsTableNested = (value) => this.setState({ isTableNested: value });
        let { transformedFilterData, fetchCount} = MediaOpsService.setCampaignsData(campaigns, this.state.isTableNested, setIsTableNested, childCampaigns, parentId, !!this.state.searchParam || this.state.filterApplied);

        if (!!this.state.searchParam || this.state.filterApplied) {
            transformedFilterData = transformedFilterData.map(item => {
                if (Array.isArray(item?.children) && item.children.length > 0) {
                    item.children = item.children.map(child => ({
                        ...child,
                        marginClass: "margin-left-20",
                        local_currency_name: item.local_currency_name,
                        parentSyntheticId: item?.synthetic_id,
                        parentStatus: item?.export_status
                    }));
                    item.nestedData = MediaOpsService.prepareCampaignData(item.children);
                } else {
                    item.nestedData = undefined;
                    item.marginClass = "margin-left-20 campaign-withno-children";
                }
                return item;
            });
        }
        this.isNestedCampaign = transformedFilterData.find(val=> val.child_count > 0) ? true : false;
        this.setState({ data: transformedFilterData, fetchCount, loaded: true}, () => {
            setTimeout(() => {
                this.props.setSpinnerCount(0);
                this.props.setLoadedCampaignData(transformedFilterData);
                if(!parentId){
                }
                this.updateTableAndScrollAfterLoad(parentId);
                if(isFreshData && this.state.hasNext){
                    this.loadMoreTableData(0);
                }
            });
        });
    }

    /**
     * The below code is to update the table, add scroll event and scroll to the last scroll position after the data is loaded
     * @returns {void}
    */ 
    updateTableAndScrollAfterLoad = (parentId) => {
        if(!parentId){
            // Add a scroll event listener to the placement tile body scroller div on initial load
            let scrollBody = document.getElementsByClassName("campaign-tile")[0]?.bodyScrollerDiv;
            scrollBody?.addEventListener('scroll', this.handleScroll);
            window?.addEventListener('resize', this.ontableResize);
    
            //Set height of the tile based on the window height
            let windowHeight = window.innerHeight;
            if(windowHeight - 150 > 0){
                document.getElementsByClassName("campaign-tile")[0].style['maxHeight'] = windowHeight - 200 + 'px';
            }
            
            // Scroll to the last scrollTop position after the data is loaded
            if(this.scrollPosition){
                scrollBody['scrollTop'] = this.scrollPosition;
            } else {
                scrollBody['scrollTop'] = 0;
            }
        }
        // Set the width of the campaign name column based on the content
        let campaignNameColumn = document.getElementsByClassName('table-section-wrapper')[0]?.shadowRoot?.querySelector('#left-table')?.shadowRoot?.querySelectorAll('.is-campaign-name');
        for(let i=0;i<campaignNameColumn?.length;i++){
            let campaignName = campaignNameColumn[i].innerText;
            let width = Utils.calculatetaxonomyWidth(campaignName);
            campaignNameColumn[i].style.width = width;
        }
        this.setState({ ispaginationCall: false})
    }

    openModal = (val) => {
        this.setState({ isAlertOpen: true, modalData: val });
    }

    /**
    * Description The above code is defining an arrow function called `onSortUpdate` that takes an event as a
    * parameter. Inside the function, it sets the spinner count to 1 using a function passed as a
    * prop. It then extracts the direction and key from the event detail and sets them in an object
    * called `sort`. It then calls a function called `MediaOpsService.sortCampaigns` with the sort key
    * and direction as parameters. If the promise returned by `MediaOpsService.sortCampaigns` is
    * resolved, it calls a function called `parseCampaignResponse` with the campaigns and a boolean
    * value as
    * @param {html event} event
    */
    onSortUpdate = (event) => {
        // this.getSortedData(event,1,[]);
    }

    /**
     * Retrieves sorted campaign data from the MediaOpsService based on the specified sorting criteria.
     * @param {Object} event - The event object that triggered the sorting.
     * @param {number} currentPageNo - The current page number being sorted.
     * @param {Array} campaignsItems - An array to store the sorted campaign items.
     * @returns {void}
     */
    getSortedData = (event,currentPageNo,campaignsItems) =>{
        this.props.setSpinnerCount(1);
        /* The below code is assigning a value to the variable `sortKey` based on the condition. If
        `this.state.isTableNested` is true, then `sortKey` is assigned the value of `event?.detail?.sort?.key`.
        Otherwise, `sortKey` is assigned the value of `event?.detail?.key`. The `?.` operator is used for
        optional chaining, which allows accessing nested properties without causing an error if any
        intermediate property is null or undefined. */
        let sortKey;
        if (this.state.isTableNested) {
            sortKey = event?.detail?.sort?.key;
        } else {
            sortKey = event?.detail?.key;
        }

        const sortDir = this.state.sort.dir.charAt(0).toUpperCase() + this.state.sort.dir.slice(1);
        MediaOpsService.sortCampaigns(sortKey, sortDir,currentPageNo, this.state.archiveTab, this.props?.versionOneApi)
            .then((campaigns) => {
                if(currentPageNo < this.state.pagesLoaded){
                    campaignsItems = campaignsItems.concat(campaigns?.data?.Items || []);
                    this.getSortedData(event,currentPageNo+1,campaignsItems)
                }else{
                    this.props.setSpinnerCount(0);
                    campaignsItems = campaignsItems.concat(campaigns?.data?.Items || []);
                    campaigns['data']['Items'] = campaignsItems;
                    this.parseCampaignResponse(campaigns, true);
                }
            }, (error) => {
                this.props.setSpinnerCount(0);
            });
    }
    /**
    * DescriptionThe above code is defining a function called `updateSearch` which takes an event as a parameter.
    * Inside the function, it sets the `searchParam` variable to the value of the event's detail
    * property. It then calls a function `setSpinnerCount` with a value of 1 as a parameter, and sets
    * the `data` state to an empty array and the `searchParam` state to the `searchParam` variable. It
    * then calls a function `MediaOpsService.searchCampaign` with the `searchParam` variable as a
    * parameter. If the function call is successful, it calls 
    * @param {html event} event
    */
    updateSearch = (event, loadMoreData = false) => {
        this.setState({ pagesLoaded: 1 });
        const searchParam = event?.detail?.value ?? this.state.searchParam;
        this.props.setSpinnerCount(1);
        this.setState({ searchParam: searchParam }, () => {
            if (searchParam) {
                MediaOpsService.searchCampaign(searchParam, this.state.archiveTab, this.props?.versionOneApi, 'campaign-name', this.state.hasNext).then(result => {
                    if (searchParam && loadMoreData) {
                        this.parseCampaignResponse(result, false, null);
                    } else {
                        this.parseCampaignResponse(result, true, null);
                    }
                    this.props.setSpinnerCount(0);
                }).catch(error => {
                    this.props.setSpinnerCount(0);
                    AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.updateSearch(event), title: `Unable to search the campaign, please refine your search text`, bodyMsgArr: [] }]);
                });
            } else {
                this.getCampaigns(true);
            }
        })
    }

    /**
    * Description The above code is defining a function called `applyFilter` that takes in an `options` parameter.
    * Within the function, it sets the spinner count to 1, clears the `data` state, sets the
    * `filterOptions` state to the `options` parameter, and then calls a `getCampaignData` function
    * from a `MediaOpsService` object with the `options` parameter, false, and true as arguments. It
    * then calls a `parseCampaignResponse` function with the `result` from the `getCampaignData`
    * function and true as arguments. Finally, it sets the
    * @param {object} options
    */
    applyFilter = (options, clearAll = false) => {
        this.setState({pagesLoaded: 1});
        options = MediaOpsService.campaignFilterOptions(options);
        this.props.setSpinnerCount(1);
        options['source'] = 'planned';
        options['page-count'] = 10;
        this.setState({ filterOptions: options, filterApplied: !clearAll })
        
        MediaOpsService.getCampaignData(options, null, this.state.archiveTab).then(result => {
            this.parseCampaignResponse(result, true);
            this.props.setSpinnerCount(0);
            trackEvent('Campaigns Filter is applied', { access_from: 'Campaign Listing Page' });
        }).catch(error => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.applyFilter(options, clearAll), title: `Unable to filter the campaign`, bodyMsgArr: []}]);
            trackEvent('Campaigns Filter has failed', { access_from: 'Campaign Listing Page', event: { error: error?.response?.data } });
        });
    }

    /**
     * Loads more table data by fetching additional pages of campaign data and updating the state.
     * It prevents the scroll position from changing during the data loading process.
     * @param {Event} event - The event that triggered the data loading.
     * @returns {void}
     */
    loadMoreTableData = (scrollTop) => {
        this.setState({ispaginationCall: true});
        this.scrollPosition = scrollTop;
        if (this.state.searchParam) {
            this.updateSearch(null, true);
        } else {
            this.getCampaigns(false);
        }
        this.setState({pagesLoaded: this.state.pagesLoaded+1});
    }

    /**
     * Function to handle the scroll event on the placement table to load more data
     * @param {Object} event - Event object
     * @returns {void}
    */ 
     handleScroll = (event) => {
        let target = event.target;
        let { scrollHeight, scrollTop, clientHeight } = target;
        if ((scrollHeight - scrollTop <= clientHeight + 5) && this.state.hasNext && !this.state.ispaginationCall && this.state.loaded) {
            this.loadMoreTableData(scrollTop);
        }
    }

    /**
     * Function to handle the page resize event on the placement table to load more data and set the height of the tile
     * @param {Object} event - Event object
     * @returns {void}
    */ 
    ontableResize = (event) => {
        let scrollBody = document.getElementsByClassName("campaign-tile")[0]?.bodyScrollerDiv;
        if(scrollBody?.scrollHeight <= scrollBody?.clientHeight && this.state.hasNext){
            this.loadMoreTableData(scrollBody.scrollTop);
        }
        //Set height of the tile based on the window height
        let windowHeight = window.innerHeight;
        if(windowHeight - 150 > 0){
            document.getElementsByClassName("campaign-tile")[0].style.maxHeight = windowHeight - 200 + 'px';
        }
    }

    // eslint-disable-next-line class-methods-use-this
    displaySkeletonTable() {
        // Per Omni-UI, the skeleton WIP table should be 6 identical rows, without the header row.
        let rows = '';
        for (let i = 0; i < 10; i++) {
            rows = rows + '<div className="table skeleton"></div>';
        }
        rows = (html`${rows}`);
        return <>{parse(rows.values[0])}</>;
    }

    /**
     * The below code is defining a function called `mergeCampaigns` in JavaScript. Inside the function, it
     * is using `this.setState` to update the state of the component by setting the `displayMergeModal`
     * property to `true`. 
     */
    openMappingMModal = (parentValue) => {
        //if client code is not assign not allowing user to merge campaign 
        if(!parentValue.synthetic_id || !parentValue.client_code){
            AlertMessagesService.showErrorToastMessage('Please update the campaign by assigning the client code');
            return
        }
        this.setState({displayMergeModal: true, selectedCampaigns: parentValue})
    }

    /**
     * Unmerges the selected campaigns.
     * 
     * This function identifies the parent campaign from the selected campaigns, constructs a payload 
     * with the necessary details, and calls the `unmergeCampaigns` API to perform the unmerge operation.
     * 
     * On success:
     * - Displays a success toast message.
     * - Resets the state and updates the campaigns list.
     * - Sets the table to a non-nested state.
     * 
     * On failure:
     * - Displays an error alert with a retry option.
     * 
     * Finally:
     * - Decrements the spinner count to indicate the operation is complete.
     * 
     * @returns {void}
     */
    unmergeCampaigns = (campaign) => {
        this.props.setSpinnerCount(1);
        // const parentCampaign = campaign.filter((campaign) => campaign.source === "planit")[0];
        let campaignId = encodeURIComponent(campaign?.id);
        let payload = {};
        if(campaign?.nestedData) {
            payload = {
                parent_synthetic_id: campaign?.synthetic_id,
                parent_id: campaign?.id,
                parent_status: campaign?.export_status,
                child_synthetic_ids: campaign?.nestedData?.filter(child => child.data_source?.toLowerCase() === "mediaocean").map(child => child.synthetic_id)
            };
        } else {
            payload = {
                parent_synthetic_id: campaign?.parentSyntheticId,
                parent_id: campaign?.parent_id,
                parent_status: campaign?.parentStatus,
                child_synthetic_ids: [campaign?.synthetic_id]
            };
        }

        MediaOpsService.unmergeCampaigns(campaignId, payload, this.props?.versionOneApi).then((response) => {
            AlertMessagesService.showSuccessToastMessage('Campaigns are unmerged!');
            this.resetStateAndUpdateCampaigns();
            this.setState({ isTableNested: false })
            trackEvent('Campaigns are unmerged.', { access_from: 'Campaign Listing Page' });
        }, (error) => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.unmergeCampaigns, title: 'Unable to unmerged the campaigns', bodyMsgArr: [] }]);
            trackEvent('Campaigns are not unmerged.', { access_from: 'Campaign Listing Page', event: { error: error?.response?.data } });
        }).finally(() => { this.props.setSpinnerCount(0) });
    }

    /**
     * The below code is defining a function called `cancelMerge` in JavaScript. This function is using the
     * `setState` method to update the state of the component by setting the `displayMergeModal` property
     * to `false`. 
     */
    cancelMerge = () => {
        this.setState({displayMergeModal: false})
    }

    /**
    * Sets the active tab and retrieves the Campaigns list if the active tab is not already set.
    * @returns {void}
    */
    setActiveTab = () => {
        this.iscampaignNestedTree = false;
        if (this.state.archiveTab) {
            this.setState({ ...this.defaultState}, this.getCampaigns);
            this.setState({isTableNested:false})
            this.scrollPosition = 0;
        }
    }

    /**
     * Sets the archive tab and triggers the retrieval of Campaigns list if the active tab is currently set.
     * @returns {void}
     */
    setArchiveTab = () => {
        this.iscampaignNestedTree = false;
        if (!this.state.archiveTab) {
            this.setState({ ...this.defaultState, archiveTab: true }, this.getCampaigns);
            this.setState({isTableNested:false})
            this.scrollPosition = 0;
        }
    }

    /**
     * On expan call child campaign for expanded parent 
     * @param {object} event 
     */
    expandOrCollapseRows = (event) => {
        if (!!this.state.searchParam || this.state.filterApplied) {
            return;
        }

        let campaign = event.detail;
        if (!campaign?.nestedData?.length) {
            this.props.setSpinnerCount(1);
            this.getCampaigns(false, campaign?.id, campaign?.camapign_id);
        } else {
            setTimeout(() => {
                this.updateTableAndScrollAfterLoad(true);
            });
        }
    }

    render() {
        return (
            <div className="media-ops-container campaign-table">
                    <OmniTileComponent className="campaign-tile" scroll heading="">
                        <OmniToolbarComponent slot="header" className='toolBar'>
                            <div slot='start'>
                                <button className={`medium activeButton ${!this.state.archiveTab ? '' : 'tab'}`} onClick={this.setActiveTab} disabled={this.props.isMappingTable}>Active Campaigns</button>
                                <button className={`medium ${this.state.archiveTab ? '' : 'tab'}`} onClick={this.setArchiveTab} disabled={this.props.isMappingTable}>Archived Campaigns</button>
                            </div>
                            <div slot="end" className="custom-dropdown-menu">
                                <OmniSearchComponent className="searchIcon search-input-icon" onSearchUpdate={this.updateSearch} slot="end" ph="Search"></OmniSearchComponent>
                                    <div className="Line-Copy-2"></div>
                                    <div className='filter-menu'>
                                        <div className="separater-vertical"></div>
                                        <CampaignFilterComponent onApplyFilter={this.applyFilter.bind(this)} />
                                    </div>
                            </div>
                        </OmniToolbarComponent>
                        {this.state.errorObject.displayError ?
                            <div style={{ minHeight: "40vh" }}><NoDataComponent heading={"Error"} message={`Unable to pull campaigns. Something went wrong". Please refresh or contact your administrator. Error code 500.`} /></div>
                            :
                            <div className="table-section"> {(this.state.loaded && this.state.data.length) ? 
                                (<>
                                    <OmniTableStickyComponent
                                        stickyside="right"
                                        data={this.state.data}
                                        leftColumns={this.leftColumns}
                                        rightColumns={this.rightColumns}
                                        sort={this.state.sort}
                                        onSortUpdate={this.onSortUpdate}
                                        onExpandOrCollapse={this.expandOrCollapseRows}
                                        key={this.state.data.length}
                                        keyFn={row => row && row._uniqueKey}
                                        expandAllOnLoad={this.state.expandAllOnLoad}
                                        className='table-section-wrapper'
                                        stickyscrollbar={true}
                                        stickyHeader={true}>
                                    </OmniTableStickyComponent>
                                    {this.state.ispaginationCall && this.displaySkeletonTable()}
                                </>)
                                : ((this.state.loaded && !this.state.data.length) ? (<div style={{ minHeight: "40vh" }}>
                                <NoDataComponent
                                    heading={"No Data Found"}
                                    message={this.state.searchParam ? `No campaign found having "${this.state.searchParam}", Please try with other search parameter` : "No campaign found for the selected filters, Please try with other filters"}/>
                                </div>) : this.displaySkeletonTable())}
                            </div>
                        }
                        <div slot='footer' className='campaign-table-footer'>
                            {`Showing ${this.state.fetchCount} of ${this.state.totalCount}`}
                        </div> 
                    </OmniTileComponent>
                {this.state.displayMergeModal && 
                    <MergeCampaigns
                        omniProfile={this.props.omniProfile}
                        navigate={this.props.navigate}
                        parentCampaign={this.state.selectedCampaigns} 
                        onCancel={this.cancelMerge} 
                        setSpinnerCount={this.props.setSpinnerCount}
                        unMapCampaign={this.state.unMapCampaign}
                        resetStateAndUpdateCampaigns={this.resetStateAndUpdateCampaigns}
                        systemParametersData={this.props.systemParametersData}
                    />
                }
            </div>
        );
    }
}